import { AiStreamFrame } from '@domain/ai-assistant/ai-assistant-stream.models';

// ---------------------------------------------------------------------------
// SSE stream reader — ported from ai-assistant-debug-ui.html
// ---------------------------------------------------------------------------

const normalizeStreamPayload = (payload: unknown): AiStreamFrame[] => {
  if (payload && typeof (payload as Record<string, unknown>)['type'] === 'string') {
    return [payload as AiStreamFrame];
  }
  if (payload && typeof (payload as Record<string, unknown>)['delta'] === 'string') {
    return [{ type: 'OUTPUT', frameType: 'DELTA', delta: (payload as Record<string, unknown>)['delta'] as string }];
  }
  if (payload && typeof (payload as Record<string, unknown>)['text'] === 'string') {
    return [{ type: 'OUTPUT', frameType: 'DELTA', delta: (payload as Record<string, unknown>)['text'] as string }];
  }
  if (typeof payload === 'string') {
    return [{ type: 'OUTPUT', frameType: 'DELTA', delta: payload }];
  }
  return [{ type: 'OUTPUT', frameType: 'DELTA', delta: JSON.stringify(payload) }];
};

const parseSseDataLine = (data: string): AiStreamFrame[] => {
  if (!data) return [];
  try {
    return normalizeStreamPayload(JSON.parse(data));
  } catch {
    return [{ type: 'OUTPUT', frameType: 'DELTA', delta: data }];
  }
};

const parseSseEvent = (eventText: string): AiStreamFrame[] => {
  const dataLines = eventText
    .split(/\r?\n/)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart());
  if (dataLines.length === 0) return [];
  return dataLines.flatMap(parseSseDataLine);
};

export const readEventStream = async (
  body: ReadableStream<Uint8Array>,
  onFrame: (frame: AiStreamFrame) => void,
  signal?: AbortSignal
): Promise<void> => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      if (signal?.aborted) break;
      const { value, done } = await reader.read();
      buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done });
      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() ?? '';
      for (const eventText of events) {
        parseSseEvent(eventText).forEach(onFrame);
      }
      if (done) {
        if (buffer.trim()) {
          parseSseEvent(buffer).forEach(onFrame);
        }
        break;
      }
    }
  } finally {
    reader.releaseLock();
  }
};
